import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { JsonBlock } from "@/components/ui/json-block";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { NvgRenderer } from "@/components/nvg/NvgRenderer";
import { useNvgBake } from "@/hooks/useNvgBake";
import {
  listPresets,
  listRegressionTestCases,
  getRegressionBaselineUrl,
} from "@/services/nvgSimulationApi";
import { useUrlState } from "@/hooks/useUrlState";
import type {
  NvgSimulationImageSize,
  NvgSimulationUrlState,
} from "@/types/pageState";
import type {
  ImageSize,
  NvgRendererHandle,
  NvgSimulationPresetInfo,
  RegressionTestCase,
} from "@/types/nvgSimulation";

// Fallback default — used only as the initial Select value before the live
// preset list loads and as the auto-submit target if it's present in that
// list. The authoritative preset keys always come from GET /presets.
const DEFAULT_PRESET = "Gen3_Standard";
const DEFAULT_TEST_CASE = "64_lp_mm_magnification";

const IMAGE_SIZE_OPTIONS: { value: string; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "256", label: "256×256" },
  { value: "512", label: "512×512" },
  { value: "1024", label: "1024×1024" },
];

function PhaseLabel({
  phase,
  pollStatus,
}: {
  phase: string;
  pollStatus: string | null;
}) {
  if (phase === "idle") return null;
  if (phase === "submitting")
    return (
      <span className="text-xs text-muted-foreground">Submitting job...</span>
    );
  if (phase === "polling")
    return (
      <span className="text-xs text-muted-foreground">
        Running ({pollStatus ?? "..."})
      </span>
    );
  if (phase === "loading_textures")
    return (
      <span className="text-xs text-muted-foreground">Loading textures...</span>
    );
  if (phase === "ready")
    return <span className="text-xs text-green-600">Ready</span>;
  return null;
}

// ============================================================================
// Preview Tab
// ============================================================================

function PreviewTab({
  imageSize,
  onImageSizeChange,
}: {
  imageSize: NvgSimulationImageSize;
  onImageSizeChange: (size: NvgSimulationImageSize) => void;
}) {
  const rendererRef = useRef<NvgRendererHandle | null>(null);
  const { state, submitPreset, reset } = useNvgBake(rendererRef);
  const [selectedPreset, setSelectedPreset] = useState(DEFAULT_PRESET);
  const [brightness, setBrightness] = useState(1.0);
  const [frozen, setFrozen] = useState(false);
  const hasAutoSubmitted = useRef(false);

  const { data: presets, isLoading: presetsLoading } = useQuery<
    Record<string, NvgSimulationPresetInfo>
  >({
    queryKey: ["nvg-presets"],
    queryFn: listPresets,
  });
  const presetOptions = presets
    ? Object.entries(presets).map(([key, p]) => ({ value: key, label: p.name }))
    : [];

  const imageSizeForApi: ImageSize | undefined =
    imageSize === "default" ? undefined : (imageSize as ImageSize);

  useEffect(() => {
    if (!hasAutoSubmitted.current && presets) {
      hasAutoSubmitted.current = true;
      const defaultKey =
        DEFAULT_PRESET in presets ? DEFAULT_PRESET : Object.keys(presets)[0];
      if (defaultKey) {
        setSelectedPreset(defaultKey);
        void submitPreset(defaultKey, imageSizeForApi);
      }
    }
  }, [presets, submitPreset, imageSizeForApi]);

  const handleRunPreset = () => {
    void submitPreset(selectedPreset, imageSizeForApi);
  };

  return (
    <div className="flex gap-4 h-full">
      {/* Left panel */}
      <div className="w-80 flex-shrink-0 flex flex-col gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Tube Preset</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Select
              value={selectedPreset}
              onValueChange={setSelectedPreset}
              disabled={presetsLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select preset" />
              </SelectTrigger>
              <SelectContent>
                {presetOptions.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">
                Image size
              </label>
              <Select
                value={imageSize === "default" ? "default" : String(imageSize)}
                onValueChange={(v) =>
                  onImageSizeChange(
                    v === "default" ? "default" : (Number(v) as ImageSize),
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMAGE_SIZE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleRunPreset}
              disabled={
                presetsLoading ||
                state.phase === "submitting" ||
                state.phase === "polling" ||
                state.phase === "loading_textures"
              }
              size="sm"
              className="w-full"
            >
              Run Preset
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => reset()}
              disabled={state.phase === "idle"}
            >
              Reset
            </Button>
            <PhaseLabel phase={state.phase} pollStatus={state.pollStatus} />
            {state.phase === "error" && (
              <p className="text-xs text-destructive">{state.error}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Controls</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">
                Brightness: {brightness.toFixed(2)}x
              </label>
              <input
                type="range"
                min="0.1"
                max="3.0"
                step="0.05"
                value={brightness}
                onChange={(e) => setBrightness(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="freeze"
                type="checkbox"
                checked={frozen}
                onChange={(e) => setFrozen(e.target.checked)}
                className="size-4"
              />
              <label htmlFor="freeze" className="text-xs text-muted-foreground">
                Freeze animation
              </label>
            </div>
          </CardContent>
        </Card>

        {state.shaderParams && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Shader Params</CardTitle>
            </CardHeader>
            <CardContent>
              <JsonBlock
                value={state.shaderParams}
                className="text-xs text-muted-foreground overflow-auto max-h-64 whitespace-pre-wrap break-all"
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Right panel: renderer */}
      <div className="flex-1 min-w-0">
        <NvgRenderer
          ref={rendererRef}
          brightnessScale={brightness}
          frozen={frozen}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Fidelity Testing Tab
// ============================================================================

function FidelityTab({
  imageSize,
  onImageSizeChange,
}: {
  imageSize: NvgSimulationImageSize;
  onImageSizeChange: (size: NvgSimulationImageSize) => void;
}) {
  const rendererRef = useRef<NvgRendererHandle | null>(null);
  const { state, submitRegression } = useNvgBake(rendererRef);
  const [selectedTestCase, setSelectedTestCase] = useState(DEFAULT_TEST_CASE);

  const imageSizeForApi: ImageSize | undefined =
    imageSize === "default" ? undefined : (imageSize as ImageSize);

  const { data: testCases, isLoading: testCasesLoading } = useQuery<
    RegressionTestCase[]
  >({
    queryKey: ["nvg-regression-test-cases"],
    queryFn: listRegressionTestCases,
  });

  const selectedTc = testCases?.find((tc) => tc.name === selectedTestCase);

  const baselineUrl = selectedTc
    ? selectedTc.has_hoffman_baseline
      ? getRegressionBaselineUrl(selectedTestCase, "hoffman")
      : selectedTc.has_cross_platform_baseline
        ? getRegressionBaselineUrl(selectedTestCase, "cross_platform")
        : null
    : null;

  const baselineLabel = selectedTc?.has_hoffman_baseline
    ? "Hoffman Physics Baseline"
    : "Cross-Platform Baseline";

  const handleRunRender = () => {
    void submitRegression(selectedTestCase, imageSizeForApi);
  };

  const isRunning = state.phase === "submitting" || state.phase === "polling";

  return (
    <div className="flex flex-col gap-4">
      {/* Controls bar */}
      <div className="flex items-center gap-3 flex-wrap">
        {testCasesLoading ? (
          <Skeleton className="h-9 w-64" />
        ) : (
          <Select value={selectedTestCase} onValueChange={setSelectedTestCase}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select test case" />
            </SelectTrigger>
            <SelectContent>
              {testCases?.map((tc) => (
                <SelectItem key={tc.name} value={tc.name}>
                  {tc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select
          value={imageSize === "default" ? "default" : String(imageSize)}
          onValueChange={(v) =>
            onImageSizeChange(
              v === "default" ? "default" : (Number(v) as ImageSize),
            )
          }
        >
          <SelectTrigger className="w-28">
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent>
            {IMAGE_SIZE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={handleRunRender} disabled={isRunning} size="sm">
          Run Render
        </Button>
        <PhaseLabel phase={state.phase} pollStatus={state.pollStatus} />
        {state.phase === "error" && (
          <span className="text-xs text-destructive">{state.error}</span>
        )}
      </div>

      {/* Test case params */}
      {selectedTc && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{selectedTc.name}</CardTitle>
            <p className="text-xs text-muted-foreground">
              {selectedTc.description}
            </p>
          </CardHeader>
          <CardContent>
            <JsonBlock
              value={selectedTc.simulator_params}
              className="text-xs text-muted-foreground overflow-auto max-h-32 whitespace-pre-wrap break-all"
            />
          </CardContent>
        </Card>
      )}

      {/* Side-by-side comparison */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            WebGL Renderer
          </p>
          <div className="relative w-full aspect-square">
            <NvgRenderer ref={rendererRef} />
            {isRunning && (
              <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
                <Skeleton className="w-full h-full" />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {baselineLabel}
          </p>
          {baselineUrl ? (
            <div className="relative w-full aspect-square bg-black rounded-lg overflow-hidden">
              <img
                src={baselineUrl}
                alt={`${baselineLabel} for ${selectedTestCase}`}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-full aspect-square bg-black rounded-lg flex items-center justify-center">
              <p className="text-zinc-600 text-sm">No baseline available</p>
            </div>
          )}
        </div>
      </div>

      {state.shaderParams && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Shader Params</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonBlock
              value={state.shaderParams}
              className="text-xs text-muted-foreground overflow-auto max-h-32 whitespace-pre-wrap break-all"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Page
// ============================================================================

export default function NvgSimulation() {
  const [urlState, setUrlState] = useUrlState<NvgSimulationUrlState>({
    schema: {
      tab: "string",
      preset: "string",
      testCase: "string",
      imageSize: "string",
    },
    defaults: { tab: "preview", imageSize: "default" },
    replace: true,
  });

  const activeTab = urlState.tab ?? "preview";
  const raw = urlState.imageSize;
  const imageSize: NvgSimulationImageSize =
    raw === "default" || raw === undefined
      ? "default"
      : [256, 512, 1024].includes(Number(raw))
        ? (Number(raw) as ImageSize)
        : "default";

  return (
    <div className="flex flex-col gap-4 p-6 h-full">
      <div className="flex items-center gap-2">
        <Eye className="size-5" />
        <h1 className="text-xl font-semibold">NVG Simulation</h1>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          setUrlState((prev) => ({ ...prev, tab: v as "preview" | "fidelity" }))
        }
        className="flex-1 flex flex-col"
      >
        <TabsList className="w-fit">
          <TabsTrigger value="preview">Simulation Preview</TabsTrigger>
          <TabsTrigger value="fidelity">Fidelity Testing</TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="flex-1 mt-4">
          <PreviewTab
            imageSize={imageSize}
            onImageSizeChange={(size) =>
              setUrlState((prev) => ({ ...prev, imageSize: size }))
            }
          />
        </TabsContent>

        <TabsContent value="fidelity" className="mt-4">
          <FidelityTab
            imageSize={imageSize}
            onImageSizeChange={(size) =>
              setUrlState((prev) => ({ ...prev, imageSize: size }))
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
